import { IconClose } from '@hulu-react-style-components/icons';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { DropdownOption } from '../../../common/Dropdown';
import Pill, { PillColor } from '../../../common/Pill/Pill';
import type { SearchParamsValuesType } from '../../../constants';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../constants';
import bem from '../../../utils/bem';
import type {
  AdditionalFilterDropdown,
  AdditionalFilterInDropdown,
} from '../TableFilterBar/AdditionalFilters/constants';
import { isDropdownOptionValues, isSimpleDropdownValues } from '../TableFilterBar/AdditionalFilters/utils';
import { getChipLabel, getChipTextValue } from './chipUtils';
import { buildRuleTree } from './ruleTree';
import RuleTreeRenderer, { buildFilterGroupMap, createFilterGroupRenderer } from './RuleTreeRenderer';

export type TopBarFilter = {
  label: string;
  searchParam: string;
  values: DropdownOption[];
};

const [, element] = bem('applied-filters-bar');

/** Visually-hidden but still rendered — Playwright innerText can read the content. */
const SR_ONLY_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const SECTION_COLLAPSED_ROWS = 3;
const SECTION_ROW_HEIGHT = 30;
const SECTION_ROW_GAP = 6;
const SECTION_COLLAPSED_MAX_HEIGHT =
  SECTION_COLLAPSED_ROWS * SECTION_ROW_HEIGHT + (SECTION_COLLAPSED_ROWS - 1) * SECTION_ROW_GAP;

const SectionOverflowWrapper: React.FC<{
  children: React.ReactNode;
  itemCount: number;
  barExpanded?: boolean;
  onOverflowChange?: (overflows: boolean) => void;
}> = ({ children, itemCount, barExpanded, onOverflowChange }) => {
  const chipsRef = useRef<HTMLDivElement>(null);
  const [isSectionOverflowing, setIsSectionOverflowing] = useState<boolean | null>(null);
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);

  useEffect(() => {
    if (barExpanded) {
      setIsSectionExpanded(true);
    }
  }, [barExpanded]);

  useLayoutEffect(() => {
    const el = chipsRef.current;
    if (!el) return;

    const needsClamp = el.scrollHeight > SECTION_COLLAPSED_MAX_HEIGHT;
    setIsSectionOverflowing(needsClamp);
    onOverflowChange?.(needsClamp);
  }, [itemCount, onOverflowChange]);

  const isClamped = isSectionOverflowing && !isSectionExpanded;

  return (
    <div className={element('filter-group-wrapper', isSectionOverflowing === false ? 'inline' : undefined)}>
      <div
        ref={chipsRef}
        className={element('filter-group-chips', isClamped ? 'clamped' : undefined)}
        style={isClamped ? { maxHeight: `${SECTION_COLLAPSED_MAX_HEIGHT}px` } : undefined}
      >
        {children}
      </div>
      {isSectionOverflowing && (
        <button
          type="button"
          className={element('section-toggle')}
          data-testid="section-toggle"
          onClick={(e) => {
            e.preventDefault();
            setIsSectionExpanded((prev) => !prev);
          }}
        >
          {isSectionExpanded ? 'View less' : 'View more'}
        </button>
      )}
    </div>
  );
};

const DropdownFilterGroup: React.FC<{
  filter: AdditionalFilterDropdown;
  onRemoveValue: (searchParam: SearchParamsValuesType, value: string) => void;
  onRemoveFilter: (searchParam: SearchParamsValuesType) => void;
  onChipClick?: (searchParam: string) => void;
  barExpanded?: boolean;
  onOverflowChange?: (overflows: boolean) => void;
}> = ({ filter, onRemoveValue, onRemoveFilter, onChipClick, barExpanded, onOverflowChange }) => {
  const { label, selectedFilterValues, searchParam, includeExcludeOptions } = filter;
  const { options } = filter.useOptions();

  const chipLabel = getChipLabel(label, searchParam, includeExcludeOptions);

  const chips = useMemo(() => {
    let rawChips: { label: string; rawValue: string }[] = [];

    if (isSimpleDropdownValues(selectedFilterValues) && selectedFilterValues.length) {
      rawChips = selectedFilterValues.map((val) => {
        const resolvedLabel =
          options.length > 0 && searchParam !== TRAFFICKING_SEARCH_PARAMS.COUNTRY_LIST
            ? options.find((opt) => opt.value === val)?.label ?? val
            : val;
        return { label: resolvedLabel, rawValue: val };
      });
    } else if (isDropdownOptionValues(selectedFilterValues) && selectedFilterValues.length) {
      rawChips = selectedFilterValues.map((opt) => ({
        label: searchParam === TRAFFICKING_SEARCH_PARAMS.COUNTRY_LIST ? opt.value : opt.label,
        rawValue: opt.value,
      }));
    }

    // KATANA-1555: Non-Prod getFilterOptions can return the same agency id more than
    // once (shared/duplicated names), so committed filter values may contain duplicate
    // ids. Collapse to one chip per unique id — otherwise the duplicated ids produce
    // duplicate React keys below, and when the applied list later shrinks React's
    // map-based reconciliation leaves the collided fibers behind as stale chips.
    const seen = new Set<string>();
    return rawChips.filter((chip) => {
      if (seen.has(chip.rawValue)) return false;
      seen.add(chip.rawValue);
      return true;
    });
  }, [selectedFilterValues, options, searchParam]);

  if (!chips.length) return null;

  return (
    <SectionOverflowWrapper itemCount={chips.length} barExpanded={barExpanded} onOverflowChange={onOverflowChange}>
      <div
        className={element('filter-group')}
        data-testid="filterChip"
        {...(onChipClick && {
          role: 'button',
          tabIndex: 0,
          onClick: () => onChipClick(searchParam),
        })}
      >
        <span className={element('filter-label')} data-testid="chip-label">
          {chipLabel}:
        </span>
        {/* V1-compatible hidden text for e2e chip-values assertions */}
        <span data-testid="chip-values" style={SR_ONLY_STYLE}>
          {chips.map((c) => c.label).join(', ')}
        </span>
        {chips.map((chip) => (
          <Pill
            key={chip.rawValue}
            color={PillColor.BLUE}
            className={element('chip')}
            textComponent={
              <span className={element('chip-content')} data-testid="filter-value-chip">
                <span className={element('chip-label')}>{chip.label}</span>
                <button
                  type="button"
                  className={element('chip-remove')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveValue(searchParam, chip.rawValue);
                  }}
                  aria-label={`remove ${chip.label}`}
                >
                  <IconClose width="10px" height="10px" />
                </button>
              </span>
            }
          />
        ))}
        {/* V1-compatible group-level remove for e2e filterChipRemove assertions */}
        <button
          type="button"
          data-testid="filterChipRemove"
          style={SR_ONLY_STYLE}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveFilter(searchParam);
          }}
          aria-label={`remove ${chipLabel} filter`}
        />
      </div>
    </SectionOverflowWrapper>
  );
};

const TopBarFilterGroup: React.FC<{
  filter: TopBarFilter;
  onRemoveTopBarValue: (searchParam: string, value: string) => void;
  onChipClick?: (searchParam: string) => void;
  barExpanded?: boolean;
  onOverflowChange?: (overflows: boolean) => void;
}> = ({ filter, onRemoveTopBarValue, onChipClick, barExpanded, onOverflowChange }) => {
  return (
    <SectionOverflowWrapper
      itemCount={filter.values.length}
      barExpanded={barExpanded}
      onOverflowChange={onOverflowChange}
    >
      <div
        className={element('filter-group')}
        data-testid="filterChip"
        {...(onChipClick && {
          role: 'button',
          tabIndex: 0,
          onClick: () => onChipClick(filter.searchParam),
        })}
      >
        <span className={element('filter-label')} data-testid="chip-label">
          {filter.label}:
        </span>
        {/* V1-compatible hidden text for e2e chip-values assertions */}
        <span data-testid="chip-values" style={SR_ONLY_STYLE}>
          {filter.values.map((o) => o.label).join(', ')}
        </span>
        {filter.values.map((option) => (
          <Pill
            key={option.value}
            color={PillColor.BLUE}
            className={element('chip')}
            textComponent={
              <span className={element('chip-content')} data-testid="filter-value-chip">
                <span className={element('chip-label')}>{option.label}</span>
                <button
                  type="button"
                  className={element('chip-remove')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveTopBarValue(filter.searchParam, option.value);
                  }}
                  aria-label={`remove ${option.label}`}
                >
                  <IconClose width="10px" height="10px" />
                </button>
              </span>
            }
          />
        ))}
        {/* V1-compatible group-level remove for e2e filterChipRemove assertions */}
        <button
          type="button"
          data-testid="filterChipRemove"
          style={SR_ONLY_STYLE}
          onClick={(e) => {
            e.stopPropagation();
            // Remove all values for this top-bar filter
            filter.values.forEach((option) => onRemoveTopBarValue(filter.searchParam, option.value));
          }}
          aria-label={`remove ${filter.label} filter`}
        />
      </div>
    </SectionOverflowWrapper>
  );
};

const NonDropdownFilterGroup: React.FC<{
  filter: Exclude<AdditionalFilterInDropdown, AdditionalFilterDropdown>;
  onRemoveFilter: (searchParam: SearchParamsValuesType) => void;
  onChipClick?: (searchParam: string) => void;
}> = ({ filter, onRemoveFilter, onChipClick }) => {
  const { label, selectedFilterValues, searchParam, includeExcludeOptions } = filter;
  const rangeOptions = 'rangeOptions' in filter ? filter.rangeOptions : undefined;

  const chipLabel = getChipLabel(label, searchParam, includeExcludeOptions);
  const textValue = useMemo(
    () =>
      getChipTextValue(
        selectedFilterValues ?? [],
        searchParam,
        rangeOptions?.valuePrefix,
        rangeOptions?.valueSuffix,
        rangeOptions?.displayMultiplier,
        rangeOptions?.valueDecimals
      ),
    [selectedFilterValues, searchParam, rangeOptions]
  );

  if (!textValue) return null;

  return (
    <div
      className={element('filter-group')}
      data-testid="filterChip"
      {...(onChipClick && {
        role: 'button',
        tabIndex: 0,
        onClick: () => onChipClick(searchParam),
      })}
    >
      <span className={element('filter-label')} data-testid="chip-label">
        {chipLabel}:
      </span>
      <span data-testid="chip-values">
        <Pill
          color={PillColor.BLUE}
          className={element('chip')}
          textComponent={
            <span className={element('chip-content')} data-testid="filter-value-chip">
              <span className={element('chip-label')}>{textValue}</span>
              <button
                type="button"
                className={element('chip-remove')}
                data-testid="filterChipRemove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFilter(searchParam);
                }}
                aria-label={`remove ${chipLabel}`}
              >
                <IconClose width="10px" height="10px" />
              </button>
            </span>
          }
        />
      </span>
    </div>
  );
};

export type FilterValueChipsProps = {
  selectedFilters: AdditionalFilterInDropdown[];
  onRemoveValue: (searchParam: SearchParamsValuesType, value: string) => void;
  onRemoveFilter: (searchParam: SearchParamsValuesType) => void;
  topBarFilters: TopBarFilter[];
  onRemoveTopBarValue: (searchParam: string, value: string) => void;
  onChipClick?: (searchParam: string) => void;
  barExpanded?: boolean;
  onAnySectionOverflow?: (hasOverflow: boolean) => void;
};

const FilterValueChips: React.FC<FilterValueChipsProps> = ({
  selectedFilters,
  onRemoveValue,
  onRemoveFilter,
  topBarFilters,
  onRemoveTopBarValue,
  onChipClick,
  barExpanded,
  onAnySectionOverflow,
}) => {
  const overflowMapRef = useRef<Record<string, boolean>>({});

  const handleOverflowChange = useCallback(
    (searchParam: string) => (overflows: boolean) => {
      const prev = overflowMapRef.current[searchParam];
      if (prev === overflows) return;
      overflowMapRef.current[searchParam] = overflows;
      const anyOverflow = Object.values(overflowMapRef.current).some(Boolean);
      onAnySectionOverflow?.(anyOverflow);
    },
    [onAnySectionOverflow]
  );

  const ruleTree = useMemo(() => buildRuleTree(topBarFilters, selectedFilters), [topBarFilters, selectedFilters]);

  const filterGroupMap = useMemo(
    () =>
      buildFilterGroupMap({
        topBarFilters,
        selectedFilters,
        renderTopBarGroup: (filter) => (
          <TopBarFilterGroup
            key={filter.searchParam}
            filter={filter}
            onRemoveTopBarValue={onRemoveTopBarValue}
            onChipClick={onChipClick}
            barExpanded={barExpanded}
            onOverflowChange={handleOverflowChange(filter.searchParam)}
          />
        ),
        renderDropdownGroup: (filter) => (
          <DropdownFilterGroup
            key={`dropdown_${filter.searchParam}_${(filter as AdditionalFilterDropdown).optionsSource ?? 'hardcoded'}`}
            filter={filter as AdditionalFilterDropdown}
            onRemoveValue={onRemoveValue}
            onRemoveFilter={onRemoveFilter}
            onChipClick={onChipClick}
            barExpanded={barExpanded}
            onOverflowChange={handleOverflowChange(filter.searchParam)}
          />
        ),
        renderNonDropdownGroup: (filter) => (
          <NonDropdownFilterGroup
            key={`other_${filter.searchParam}`}
            filter={filter as Exclude<AdditionalFilterInDropdown, AdditionalFilterDropdown>}
            onRemoveFilter={onRemoveFilter}
            onChipClick={onChipClick}
          />
        ),
      }),
    [
      topBarFilters,
      selectedFilters,
      onRemoveTopBarValue,
      onRemoveValue,
      onRemoveFilter,
      onChipClick,
      barExpanded,
      handleOverflowChange,
    ]
  );

  const renderFilterGroup = useMemo(() => createFilterGroupRenderer(filterGroupMap), [filterGroupMap]);

  return <RuleTreeRenderer ruleTree={ruleTree} renderFilterGroup={renderFilterGroup} />;
};

export default FilterValueChips;
